import { AxiosError } from "axios";
import { useMemo } from "react";
import useSWRImmutable from "swr/immutable";
import {
  GetMarkets200Response,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { APIEndpointEnum } from "../../enums/endpoint";
import { useLoggerMiddleware } from "../../hooks/api/middleware/useLoggerMiddleware";
import { publicApi } from "../../services/api/apiFetcher";
import { AssetResponse } from "../../utils/asset";
import { registerMarketDecimals } from "../../utils/rawConversion";

export interface IPerpsMarket
  extends Omit<GetMarkets200Response, "expiry" | "strike"> {}

// In option market, strike and expiry is required
export interface IOptionMarket extends GetMarkets200Response {
  strike: string;
  /**
   * Only for option instruments. Expiry of the option contract in seconds. Eg. \"1680249600\"
   * @type {string}
   * @memberof IOptionMarket
   */
  expiry: string;
}

export const useGetMarkets = (
  asset?: AssetResponse,
  instrumentType?: InstrumentTypeResponse,
  disabled?: boolean
) => {
  const fetcher = publicApi();
  const swr = useSWRImmutable<GetMarkets200Response[], AxiosError>(
    !disabled ? [APIEndpointEnum.MARKETS, asset, instrumentType] : undefined,
    async () => {
      const data = (await (await fetcher.getMarkets(asset, instrumentType))()).data;
      // Register market decimals for raw value conversion.
      //
      // Only what the API actually sent: `quote_decimals || 18` turned a
      // legitimate 0 into 18, and invented an 18 whenever the field was
      // missing — a guess that then looked like a reading to every consumer.
      // A market that does not declare both is left unregistered, and the
      // conversion layer reports it as unknown rather than scaling by a guess.
      data.forEach((d: any) => {
        if (d.base_decimals == null || d.quote_decimals == null) return;
        const base = Number(d.base_decimals);
        const quote = Number(d.quote_decimals);
        if (!Number.isFinite(base) || !Number.isFinite(quote)) return;
        // String(), because `d` is `any` and `registerMarketDecimals` calls
        // `.toLowerCase()` unguarded. A numeric id would throw INSIDE the SWR
        // fetcher, rejecting the whole /markets request -- no markets, no
        // decimals, no order book, app-wide, from one row. The OpenAPI model
        // says these are strings, so this is a guard rather than a fix for an
        // observed failure; realtimeOpenOrders.ts already wraps the same value.
        if (d.instrument_name) {
          registerMarketDecimals(String(d.instrument_name), base, quote);
        }
        if (d.instrument_id) {
          registerMarketDecimals(String(d.instrument_id), base, quote);
        }
      });
      return data.map((d) => ({
        ...d,
        expiry: d.expiry,
      }));
    },
    {
      use: [useLoggerMiddleware],
    }
  );

  const activeMarkets: GetMarkets200Response[] | undefined = useMemo(
    () => swr.data?.filter((m) => m.is_active),
    [swr.data]
  );

  return {
    ...swr,
    activeMarkets,
  };
};
